import React, { useState, useEffect } from "react";
import { Container, Card, Table, Button, Badge, Modal, Form, Row, Col } from "react-bootstrap";
import { FaPlus } from "react-icons/fa";
import { api } from "../../config/api";
import { getAllInventoryApi } from "./service/api_service";
import Swal from "sweetalert2";
import { useAuth } from "../authen/AuthContext";

export default function TransferStock() {
  const { user: currentUser } = useAuth();
  const token = localStorage.getItem("token");
  
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  
  const [inventoryItems, setInventoryItems] = useState([]);

  useEffect(() => {
    fetchTransfers();
    fetchInventory();
  }, []);

  const fetchTransfers = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/transfers', { headers: { Authorization: `Bearer ${token}` } });
      setTransfers(res.data || []);
    } catch (error) {
      console.error("Failed to fetch transfers:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInventory = async () => {
    try {
      const res = await getAllInventoryApi(token);
      setInventoryItems(res.data || []);
    } catch (error) {
      console.error("Failed to load inventory:", error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString('th-TH');
  };

  return (
    <Container fluid className="px-3 py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="fw-bold mb-0" style={{ color: "#1e3c72" }}>Inter-Branch Transfers</h4>
        <Button 
          variant="primary" 
          style={{ background: "#1e3c72", border: "none" }}
          onClick={() => setShowModal(true)}
        >
          <FaPlus className="me-2" />
          New Transfer
        </Button>
      </div>

      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle">
              <thead className="bg-light">
                <tr>
                  <th className="py-3 px-4 border-0">Transfer No.</th>
                  <th className="py-3 px-4 border-0">Date</th>
                  <th className="py-3 px-4 border-0">From Branch</th>
                  <th className="py-3 px-4 border-0">To Branch</th>
                  <th className="py-3 px-4 border-0">Reason</th>
                  <th className="py-3 px-4 border-0">Created By</th>
                </tr>
              </thead>
              <tbody>
                {transfers.length > 0 ? (
                  transfers.map(doc => (
                    <tr key={doc.id}>
                      <td className="px-4 fw-bold text-primary">#TRF-{doc.id}</td>
                      <td className="px-4 text-muted">{formatDate(doc.created_at)}</td>
                      <td className="px-4"><Badge bg="secondary">{doc.source_branch_name}</Badge></td>
                      <td className="px-4"><Badge bg="success">{doc.target_branch_name}</Badge></td>
                      <td className="px-4 text-muted">{doc.reason}</td>
                      <td className="px-4">{doc.created_by_name || 'System'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-5 text-muted">
                      No transfers found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <NewTransferModal 
        show={showModal} 
        onHide={() => setShowModal(false)}
        onSuccess={fetchTransfers}
        inventoryItems={inventoryItems}
        currentUser={currentUser}
        token={token}
      />
    </Container>
  );
}

function NewTransferModal({ show, onHide, onSuccess, inventoryItems, currentUser, token }) {
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    source_branch_id: currentUser?.default_branch_id || "",
    target_branch_id: "",
    reason: "",
    items: [{ inventory_item_id: "", quantity: "" }]
  });

  // Reset form every time modal opens
  useEffect(() => {
    if (show) {
      setFormData({
        source_branch_id: currentUser?.default_branch_id || "",
        target_branch_id: "",
        reason: "",
        items: [{ inventory_item_id: "", quantity: "" }]
      });
    }
  }, [show, currentUser]);

  const handleAddItem = () => {
    setFormData({ ...formData, items: [...formData.items, { inventory_item_id: "", quantity: "" }] });
  };

  const handleRemoveItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  // Get already-selected item IDs for duplicate prevention
  const selectedItemIds = formData.items.map(i => String(i.inventory_item_id)).filter(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (String(formData.source_branch_id) === String(formData.target_branch_id)) {
      Swal.fire("ข้อผิดพลาด", "ไม่สามารถโอนย้ายไปยังสาขาเดียวกันได้", "error");
      return;
    }

    // Check for duplicate items
    const itemIds = formData.items.map(i => i.inventory_item_id).filter(Boolean);
    const uniqueIds = new Set(itemIds);
    if (uniqueIds.size !== itemIds.length) {
      Swal.fire("ข้อผิดพลาด", "มีรายการวัตถุดิบซ้ำกัน กรุณาตรวจสอบอีกครั้ง", "error");
      return;
    }

    try {
      setSaving(true);
      await api.post('/api/transfers', formData, { headers: { Authorization: `Bearer ${token}` } });
      Swal.fire("สำเร็จ", "บันทึกการโอนย้ายเรียบร้อย", "success");
      onSuccess();
      onHide();
    } catch (err) {
      Swal.fire("ข้อผิดพลาด", err.response?.data?.message || err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fw-bold" style={{ color: "#1e3c72" }}>New Inter-Branch Transfer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3 g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small fw-bold text-muted">From Branch (Source)</Form.Label>
                <Form.Select 
                  required 
                  value={formData.source_branch_id}
                  onChange={(e) => setFormData({...formData, source_branch_id: e.target.value})}
                >
                  <option value="">Select Branch...</option>
                  {currentUser?.allowed_branches?.map(b => {
                    const bId = b.id || b;
                    const bName = b.name || `Branch ${b}`;
                    return <option key={bId} value={bId}>{bName}</option>;
                  })}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small fw-bold text-muted">To Branch (Target)</Form.Label>
                <Form.Select 
                  required 
                  value={formData.target_branch_id}
                  onChange={(e) => setFormData({...formData, target_branch_id: e.target.value})}
                >
                  <option value="">Select Branch...</option>
                  {currentUser?.allowed_branches?.map(b => {
                    const bId = b.id || b;
                    const bName = b.name || `Branch ${b}`;
                    if (String(bId) === String(formData.source_branch_id)) return null;
                    return <option key={bId} value={bId}>{bName}</option>;
                  })}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-4">
            <Form.Label className="small fw-bold text-muted">Reason / Note</Form.Label>
            <Form.Control 
              type="text" 
              placeholder="e.g. Replenish stock, emergency transfer" 
              value={formData.reason}
              onChange={(e) => setFormData({...formData, reason: e.target.value})}
            />
          </Form.Group>

          <h6 className="fw-bold border-bottom pb-2 mb-3">Items to Transfer</h6>
          
          {formData.items.map((item, index) => (
            <Row key={index} className="mb-2 align-items-center g-2">
              <Col md={7}>
                <Form.Select 
                  required 
                  size="sm"
                  value={item.inventory_item_id}
                  onChange={(e) => handleItemChange(index, "inventory_item_id", e.target.value)}
                >
                  <option value="">Select Item...</option>
                  {inventoryItems.map(inv => {
                    const isSelected = selectedItemIds.includes(String(inv.id)) && String(inv.id) !== String(item.inventory_item_id);
                    return (
                      <option key={inv.id} value={inv.id} disabled={isSelected}>
                        {inv.name} (คงเหลือ: {inv.stock_quantity ?? 0} {inv.unit || ''})
                      </option>
                    );
                  })}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Control 
                  required 
                  size="sm"
                  type="number" 
                  step="0.01" 
                  min="0.01" 
                  placeholder="Qty" 
                  value={item.quantity}
                  onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                />
              </Col>
              <Col md={2}>
                <Button variant="outline-danger" size="sm" className="w-100" onClick={() => handleRemoveItem(index)} disabled={formData.items.length === 1}>
                  Remove
                </Button>
              </Col>
            </Row>
          ))}

          <Button variant="link" size="sm" className="text-decoration-none mt-2 p-0" onClick={handleAddItem}>
            <FaPlus className="me-1" /> Add Another Item
          </Button>

        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="secondary" onClick={onHide}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? "Saving..." : "Confirm Transfer"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
